import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { Plus, Trash2, X, Pencil } from "lucide-react";

const empty = { plate: "", model: "", capacity_kg: 0, type: "", active: true };

export default function Vehicles() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(empty);
  const [error, setError] = useState("");

  const fetchRows = async () => {
    const { data } = await api.get("/vehicles");
    setRows(data);
  };
  useEffect(() => { fetchRows(); }, []);

  const openCreate = () => {
    setEditId(null);
    setForm(empty);
    setError("");
    setOpen(true);
  };
  const openEdit = (row) => {
    setEditId(row.id);
    setForm({
      plate: row.plate || "",
      model: row.model || "",
      capacity_kg: row.capacity_kg || 0,
      type: row.type || "",
      active: row.active !== false,
    });
    setError("");
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const payload = { ...form, capacity_kg: Number(form.capacity_kg) };
      if (editId) {
        await api.put(`/vehicles/${editId}`, payload);
      } else {
        await api.post("/vehicles", payload);
      }
      setOpen(false);
      setEditId(null);
      setForm(empty);
      fetchRows();
    } catch (err) { setError(formatApiError(err)); }
  };
  const del = async (id) => {
    if (!window.confirm("Fahrzeug wirklich löschen?")) return;
    await api.delete(`/vehicles/${id}`);
    fetchRows();
  };

  return (
    <Layout>
      <div className="px-6 md:px-10 py-8 max-w-[1400px] mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="label-eyebrow" style={{ color: "#D4AF37" }}>Stammdaten</div>
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mt-2">Fahrzeuge</h1>
          </div>
          <button onClick={openCreate} data-testid="vehicle-add-button" className="btn-gold px-5 py-3 rounded-sm text-sm uppercase tracking-wider flex items-center gap-2">
            <Plus size={16} strokeWidth={1.8} /> Neues Fahrzeug
          </button>
        </div>

        <div className="card mt-6 overflow-x-auto">
          <table className="w-full text-sm min-w-[600px]">
            <thead>
              <tr className="border-b border-[var(--border)] text-[var(--text-3)] uppercase text-[11px] tracking-wider">
                <th className="text-left py-3 px-4">Kennzeichen</th>
                <th className="text-left py-3 px-4">Modell</th>
                <th className="text-left py-3 px-4">Typ</th>
                <th className="text-left py-3 px-4">Kapazität</th>
                <th className="text-left py-3 px-4">Aktiv</th>
                <th className="text-right py-3 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={6} className="text-center py-10 text-[var(--text-3)]">Keine Fahrzeuge erfasst.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-[var(--border)]" data-testid={`vehicle-row-${r.id}`}>
                  <td className="py-3 px-4 font-mono text-[var(--text)]">{r.plate}</td>
                  <td className="py-3 px-4 text-[var(--text)]">{r.model || "—"}</td>
                  <td className="py-3 px-4 text-[var(--text-2)]">{r.type || "—"}</td>
                  <td className="py-3 px-4 font-mono text-[var(--text-2)]">{r.capacity_kg ? `${r.capacity_kg} kg` : "—"}</td>
                  <td className="py-3 px-4 text-[var(--text)]">{r.active ? "Ja" : "Nein"}</td>
                  <td className="py-3 px-4 text-right">
                    <button onClick={() => openEdit(r)} data-testid={`vehicle-edit-${r.id}`} className="text-[var(--text-3)] hover:text-[#D4AF37] mr-3"><Pencil size={16} strokeWidth={1.5} /></button>
                    <button onClick={() => del(r.id)} data-testid={`vehicle-delete-${r.id}`} className="text-[var(--text-3)] hover:text-red-700"><Trash2 size={16} strokeWidth={1.5} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="card w-full max-w-xl p-6 fade-in">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">{editId ? "Fahrzeug bearbeiten" : "Neues Fahrzeug"}</h2>
              <button onClick={() => setOpen(false)} className="text-[var(--text-3)] hover:text-[var(--text)]"><X size={18} /></button>
            </div>
            {error && <div className="border border-red-500/30 bg-red-500/5 text-red-700 text-sm px-3 py-2 rounded-sm mb-4">{error}</div>}
            <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-3">
              <div><label className="label-eyebrow block mb-2">Kennzeichen *</label><input required data-testid="vehicle-form-plate" className="input-base" value={form.plate} onChange={(e) => setForm({...form, plate: e.target.value})} /></div>
              <div><label className="label-eyebrow block mb-2">Modell</label><input className="input-base" value={form.model} onChange={(e) => setForm({...form, model: e.target.value})} /></div>
              <div><label className="label-eyebrow block mb-2">Typ</label><input className="input-base" placeholder="LKW, Lieferwagen…" value={form.type} onChange={(e) => setForm({...form, type: e.target.value})} /></div>
              <div><label className="label-eyebrow block mb-2">Kapazität (kg)</label><input type="number" className="input-base" value={form.capacity_kg} onChange={(e) => setForm({...form, capacity_kg: e.target.value})} /></div>
              <div className="col-span-2 flex items-center gap-2">
                <input type="checkbox" id="vehicle-active" data-testid="vehicle-form-active" checked={form.active} onChange={(e) => setForm({...form, active: e.target.checked})} />
                <label htmlFor="vehicle-active" className="text-sm text-[var(--text)]">Aktiv</label>
              </div>
              <div className="col-span-2 flex justify-end gap-2 mt-2">
                <button type="button" onClick={() => setOpen(false)} className="btn-ghost px-4 py-2 text-sm rounded-sm uppercase tracking-wider">Abbrechen</button>
                <button type="submit" data-testid="vehicle-form-submit" className="btn-gold px-4 py-2 text-sm rounded-sm uppercase tracking-wider">{editId ? "Speichern" : "Anlegen"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
